import type { CropTransformKeyframe, EaseType } from './types';

/**
 * Computes a centered cover crop for fitting source media into a canvas.
 * Returns a crop that fills the canvas while maintaining aspect ratio.
 *
 * Uses the same math as MediaPositioner + creatomateToNormalizedCrop to ensure
 * consistency with aspect ratio change operations.
 *
 * @param canvasWidth Width of the target canvas
 * @param canvasHeight Height of the target canvas
 * @param sourceWidth Natural width of the source media
 * @param sourceHeight Natural height of the source media
 * @returns Normalized crop coordinates, or null if aspect ratios match
 */
export function computeCoverCrop(
  canvasWidth: number,
  canvasHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): CropTransformKeyframe['crop'] | null {
  const canvasAspectRatio = canvasWidth / canvasHeight;
  const sourceAspectRatio = sourceWidth / sourceHeight;

  // If aspect ratios match, no cropping needed
  if (Math.abs(sourceAspectRatio - canvasAspectRatio) < 0.001) {
    return null;
  }

  // Compute width percentage using cover behavior:
  // Scale up to fill the canvas while maintaining aspect ratio
  const widthPercent = Math.max(
    100,
    (sourceAspectRatio / canvasAspectRatio) * 100,
  );

  // Height is always 100% for cover (we fill vertically)
  // Position is always centered (50, 50)
  return creatomateToNormalizedCrop(50, 50, widthPercent, 100);
}

// Converts normalized crop box coordinates (0-1) to Creatomate center-based percentages
export function cropToCreatomate(
  cropX: number,
  cropY: number,
  cropW: number,
  cropH: number,
): { x: number; y: number; width: number; height: number } {
  const cropXPercent = cropX * 100;
  const cropYPercent = cropY * 100;
  const cropWPercent = cropW * 100;
  const cropHPercent = cropH * 100;

  const newWidth = 10000 / cropWPercent;
  const newHeight = 10000 / cropHPercent;

  const cropCenterX = cropXPercent + cropWPercent / 2;
  const cropCenterY = cropYPercent + cropHPercent / 2;

  const newX = 50 - (cropCenterX - 50) * (newWidth / 100);
  const newY = 50 - (cropCenterY - 50) * (newHeight / 100);

  return { x: newX, y: newY, width: newWidth, height: newHeight };
}

// Inverse of cropToCreatomate: Creatomate center-based rect → normalized crop box (0-1)
export function creatomateToNormalizedCrop(
  xPercent: number,
  yPercent: number,
  widthPercent: number,
  heightPercent: number,
): CropTransformKeyframe['crop'] {
  const cropWidth = 10000 / widthPercent;
  const cropHeight = 10000 / heightPercent;

  const cropCenterX = 50 - (xPercent - 50) * (100 / widthPercent);
  const cropCenterY = 50 - (yPercent - 50) * (100 / heightPercent);

  return {
    x: (cropCenterX - cropWidth / 2) / 100,
    y: (cropCenterY - cropHeight / 2) / 100,
    w: cropWidth / 100,
    h: cropHeight / 100,
  };
}

/**
 * Creates a default crop/transform keyframe anchored at clip start.
 * The crop is derived from legacy adjustModificationsToAspectRatio logic
 * to maintain compatibility with portrait/square aspect ratio behavior.
 */
export interface DefaultCropKeyframeOptions {
  crop: CropTransformKeyframe['crop'];
  ease?: EaseType;
}

export function createDefaultCropKeyframe(
  options: DefaultCropKeyframeOptions,
): CropTransformKeyframe {
  const { crop, ease = 'linear' } = options;

  return {
    timelineAnchor: 'clip-start',
    crop,
    scale: 1,
    anchor: { x: 0.5, y: 0.5 },
    ease,
  };
}
